import type { RemoteI9ConfirmDocumentRequest } from '@dtos/remote-i9-confirm-document';
import { RemoteI9DocumentListResponse } from '@dtos/remote-i9-document-list';
import { ErrorTypes } from '@models/Errors';
import { IConfigurationValuesService } from '@services/ConfigurationValuesService';
import { IErrorReportingService } from '@services/error-reporting-service';
import { RequireAuthenticationToken } from '@services/http/authorisation-interceptor';
import { IHttpService } from '@services/HttpService';
import { IObjectUrlService } from '@services/object-url-service';
import { I9DocumentInfoDto } from '@dtos/I9Form';
import { RemoteI9StatusDetails } from '@dtos/remote-i9-status';

export interface IDocumentReviewApiService {
    /**
     * Retrieves the list of I-9 documents to be reviewed by the authorised representative.
     */
    fetchI9DocumentsAsync(): Promise<RemoteI9DocumentListResponse>;
    /**
     * Retrieves the I-9 Document specified and provides an object URL with the data
     *
     * Important: This object URL should be revoked when no longer in use.
     * @param id
     */
    downloadI9DocumentAsync(id: string): Promise<string>;
    /**
     * Attempts to confirm the documents supplied in the request
     * @param request
     */
    confirmDocuments(request: RemoteI9ConfirmDocumentRequest): Promise<void>;

    getDocumentTypesValidation(request: {
        documentTypes: string[];
        includeDisabled: boolean;
    }): Promise<I9DocumentInfoDto[]>;

    getWorkerStatus(): Promise<RemoteI9StatusDetails>;
}

export enum DocumentReviewEndpoint {
    documentList = '/documents',
    confirmDocuments = '/documents/confirm',
    downloadDocument = '/document/download',
    documentType = '/document/validation/documentType',
    status = '/status'
}

export class DocumentReviewApiService implements IDocumentReviewApiService {
    get workerOnboardingUrlPrefix(): string {
        return `${this.configurationValuesService.apiUrl}${DocumentReviewApiService.workerOnboardingBasePath}`;
    }

    get workerDocumentViewUrlPrefix(): string {
        return `${this.configurationValuesService.apiUrl}${DocumentReviewApiService.workerDocumentViewBasePath}`;
    }
    static readonly workerOnboardingBasePath: string =
        '/worker-onboarding/external/representative';
    static readonly workerDocumentViewBasePath: string =
        '/worker-document-view/external/representative';

    static readonly errorReportingContext = {
        tags: {
            service: 'DocumentReviewApiService'
        }
    };

    constructor(
        private httpService: IHttpService,
        private configurationValuesService: IConfigurationValuesService,
        private objectUrlService: IObjectUrlService,
        private errorReportingService: IErrorReportingService
    ) {}

    private parseNotFoundError(error: any) {
        if (
            error.response?.status === 400 &&
            error.response?.data?.errorCode === ErrorTypes.NotFound
        ) {
            throw new Error(ErrorTypes.NotFound);
        }
    }

    /**
     * Retrieves the I-9 Document specified and provides an object URL with the data
     *
     * Important: This object URL should be revoked when no longer in use.
     * @param id
     */
    @RequireAuthenticationToken
    async downloadI9DocumentAsync(id: string): Promise<string> {
        try {
            const url = `${this.workerDocumentViewUrlPrefix}${DocumentReviewEndpoint.downloadDocument}/${id}`;
            const response = await this.httpService.get<BlobPart>(url, {
                responseType: 'blob'
            });
            return this.objectUrlService.createObjectUrl(
                new Blob([response.data])
            );
        } catch (error: unknown) {
            this.errorReportingService.reportError(
                error,
                DocumentReviewApiService.errorReportingContext
            );
            this.parseNotFoundError(error);
            throw new Error(
                `${ErrorTypes.ErrorOther} - ${(error as Error).message}`
            );
        }
    }

    @RequireAuthenticationToken
    async fetchI9DocumentsAsync(): Promise<RemoteI9DocumentListResponse> {
        try {
            const url = `${this.workerOnboardingUrlPrefix}${DocumentReviewEndpoint.documentList}`;
            const response =
                await this.httpService.get<RemoteI9DocumentListResponse>(url);
            return response.data;
        } catch (error: unknown) {
            this.errorReportingService.reportError(
                error,
                DocumentReviewApiService.errorReportingContext
            );
            this.parseNotFoundError(error);
            throw new Error(
                `${ErrorTypes.ErrorOther} - ${(error as Error).message}`
            );
        }
    }

    @RequireAuthenticationToken
    async getDocumentTypesValidation(request: {
        documentTypes: string[];
        includeDisabled: boolean;
    }): Promise<I9DocumentInfoDto[]> {
        try {
            const url = `${this.workerOnboardingUrlPrefix}${DocumentReviewEndpoint.documentType}`;
            const response = await this.httpService.post(url, request);
            return response.data;
        } catch (error: unknown) {
            this.errorReportingService.reportError(
                error,
                DocumentReviewApiService.errorReportingContext
            );
            this.parseNotFoundError(error);
            throw new Error(
                `${ErrorTypes.ErrorOther} - ${(error as Error).message}`
            );
        }
    }

    @RequireAuthenticationToken
    async confirmDocuments(
        request: RemoteI9ConfirmDocumentRequest
    ): Promise<void> {
        try {
            const url = `${this.workerOnboardingUrlPrefix}${DocumentReviewEndpoint.confirmDocuments}`;
            await this.httpService.post<any, RemoteI9ConfirmDocumentRequest>(
                url,
                request
            );
        } catch (error: unknown) {
            this.errorReportingService.reportError(
                error,
                DocumentReviewApiService.errorReportingContext
            );
            this.parseNotFoundError(error);
            throw new Error(
                `${ErrorTypes.ErrorOther} - ${(error as Error).message}`
            );
        }
    }

    @RequireAuthenticationToken
    async getWorkerStatus(): Promise<RemoteI9StatusDetails> {
        try {
            const url = `${this.workerOnboardingUrlPrefix}${DocumentReviewEndpoint.status}`;
            const response = await this.httpService.get(url);
            return response.data;
        } catch (error: unknown) {
            this.errorReportingService.reportError(
                error,
                DocumentReviewApiService.errorReportingContext
            );
            this.parseNotFoundError(error);
            throw new Error(
                `${ErrorTypes.ErrorOther} - ${(error as Error).message}`
            );
        }
    }
}



// WEBPACK FOOTER //
// ./src/services/document-review-api-service.ts